import { useTranslation } from "react-i18next";
import { Route } from "@/routes/visits/$visitId";
import { useCallback, useEffect, useState } from "react";
import { useVisitStore } from "@/store/visit.store";
import { useAuthStore } from "@/store/auth.store";
import { MultiRecordingSidebar } from "@/components/ui/visits/multi-recording-sidebar";
import { TranscriptionsCard } from "@/components/ui/visits/transcriptions-card";
import { EditTitleDialog } from "../../components/ui/visit/EditTitleDialog";
import { Layout } from "@/components/layout";
import { VisitHeader } from "../../components/ui/visit/VisitHeader";
import { VisitTemplateViewer } from "./components/VisitTemplateViewer";
import { useRecordingUsageStore } from "@/store/recordingUsage.store";
import { getFormattedDateToHour } from "@/lib/time";

export const Visit = (): JSX.Element => {
  const { t } = useTranslation();
  const { useParams } = Route;
  const { visitId } = useParams<{ visitId: string }>();
  const [expandedItems, setExpandedItems] = useState<number[]>([]);
  const [isMultiRecordingOpen, setIsMultiRecordingOpen] = useState(false);
  const [showEditTitleModal, setShowEditTitleModal] = useState(false);
  const [visitTitle, setVisitTitle] = useState<string>("");
  const { fetchAndSetAvailableMinutes } = useRecordingUsageStore.getState();
  const { getUser, user, userData } = useAuthStore();
  const {
    getFile,
    file,
    notes,
    loading,
    getMultiRecordingFiles,
    multiRecordingFiles,
    updateFile,
  } = useVisitStore();

  useEffect(() => {
    if (user?.email) {
      void getUser(user.email);
    }
    if (userData?.id) {
      void getFile({ userId: userData.id, fileId: Number(visitId) });
      void getMultiRecordingFiles({ fileId: Number(visitId) });
      void fetchAndSetAvailableMinutes(userData.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (file) {
      setVisitTitle(file.title || `${t("visit.title")} ${file.rowno}`);
    }
  }, [file, t]);

  useEffect(() => {
    if (!userData) return undefined;
    const intervalId = setInterval(() => {
      void getFile({ userId: userData.id, fileId: Number(visitId) }, false);
      void getMultiRecordingFiles({ fileId: Number(visitId) }, false);
    }, 10000);
    return (): void => {
      clearInterval(intervalId);
    };
  }, [getFile, getMultiRecordingFiles, userData, visitId]);

  const toggleExpanded = (id: number): void => {
    setExpandedItems((previous) =>
      previous.includes(id)
        ? previous.filter((x) => x !== id)
        : [...previous, id]
    );
  };

  const updateFileTitle = async (): Promise<void> => {
    if (file && !!visitTitle.length) {
      await updateFile({ fileId: file.id, title: visitTitle }, false);
      setShowEditTitleModal(false);
      if (userData) {
        void getFile({ userId: userData.id, fileId: Number(visitId) }, false);
      }
    }
  };

  const refreshVisit = useCallback(() => {
    if (userData) {
      void getFile({ userId: userData.id, fileId: Number(visitId) }, true);
    }
  }, [userData, getFile, visitId]);

  return (
    <Layout
      isLoading={loading}
      navbarTitle={t("visit.title")}
      breadcrumbs={[
        { name: t("visits.title"), href: `/visits`, current: false },
        ...(file?.rowno
          ? [
              {
                name: visitTitle,
                href: `/visits/${visitId}`,
                current: true,
              },
            ]
          : []),
      ]}
    >
      <div className="flex min-h-screen">
        <div className="flex-1 space-y-4 p-8 pt-6">
          <VisitHeader
            title={visitTitle}
            onAddRecording={() => {
              setIsMultiRecordingOpen(true);
            }}
            onEditTitleClick={() => {
              setShowEditTitleModal(true);
            }}
          />
          <div className="flex justify-between">
            <span className="text-base">
              {t("visit.created")}
              {": "}
              {file && getFormattedDateToHour(file?.createDate)}
            </span>
            <span className="opacity-0">{t("visit.lastModified")}</span>
          </div>
          <TranscriptionsCard
            expandedItems={expandedItems}
            file={file}
            multiRecordingFiles={multiRecordingFiles}
            toggleExpanded={toggleExpanded}
          />
          {file && (
            <VisitTemplateViewer
              file={file}
              notes={notes}
              refresh={refreshVisit}
            />
          )}
        </div>
        <MultiRecordingSidebar
          open={isMultiRecordingOpen}
          onOpenChange={setIsMultiRecordingOpen}
        />
        <EditTitleDialog
          isOpen={showEditTitleModal}
          title={visitTitle}
          onChangeTitle={setVisitTitle}
          onSave={updateFileTitle}
          onCancel={() => {
            setShowEditTitleModal(false);
          }}
        />
      </div>
    </Layout>
  );
};
